import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import SafeUncontrolledPopover from '../../../components/SafeUncontrolledPopover';
import { CampaignWithConfigs } from '../../../types';
import { getDatePart, parseDateFromUnicode } from '../../../utils/util/time';
import { INPUT_TYPES } from '../../Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from '../../Widgets/Modals/ModalEditor';

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  campaign: CampaignWithConfigs;
  isRegeneration?: boolean;
  onSuccess: (dataset_id?: number) => void;
};

type ApiResponse = {
  dataset_id: number;
};

const CampaignParticipantsSnapshotEditorModal: FC<Props> = ({
  isOpen,
  onToggle,
  campaign,
  isRegeneration = false,
  onSuccess,
}) => {
  const handleCallback = useCallback(
    (data?: ApiResponse, error?: unknown, hardErrorMessage?: unknown) => {
      if (!error && !hardErrorMessage && data) {
        if (isRegeneration) {
          onSuccess();
        } else if (data) {
          onSuccess(data.dataset_id);
        }
      }
    },
    [onSuccess, isRegeneration]
  );

  const inputFields = [
    {
      name: 'cutoff_date',
      label:
        'Only people who started on or before the following date should participate',
      type: INPUT_TYPES.DATE_PICKER,
      autoFocus: false,
      maxDate: new Date(),
      required: true,
    },
  ];

  const object = {
    cutoff_date: parseDateFromUnicode(
      campaign.coverage_start_date,
      'yyyy-MM-dd'
    ),
  };

  return (
    <>
      <ModalEditor
        title={
          isRegeneration ? (
            <FormattedMessage
              id="app.views.administration.campaign_participants_snapshot_editor_modal.regenerate_title"
              defaultMessage="Regenerate all cycle participants for {cycle_name}?"
              values={{ cycle_name: campaign.name }}
            />
          ) : (
            <FormattedMessage
              id="app.views.administration.campaign_participants_snapshot_editor_modal.snapshot_title"
              defaultMessage="Create participants snapshot for {cycle_name}"
              values={{ cycle_name: campaign.name }}
            />
          )
        }
        callback={handleCallback}
        isOpen={isOpen}
        toggle={onToggle}
        method="POST"
        url={
          isRegeneration
            ? `/campaigns/${campaign.id}/regenerate-participants`
            : `/datasets/${campaign.dataset_id}/snapshot`
        }
        submitText={
          <FormattedMessage
            id="app.views.administration.campaign_participants_snapshot_editor_modal.button.create"
            defaultMessage="Create Snapshot"
          />
        }
        object={object}
        inputs={inputFields}
        transformObjectBeforeSubmit={(payload) => {
          return {
            ...payload,
            cutoff_date: getDatePart(payload.cutoff_date),
          };
        }}
        renderForm={(inputs, button) => {
          return (
            <>
              <div className="mb-4">
                {isRegeneration ? (
                  <FormattedMessage
                    id="app.views.administration.campaign_participants_snapshot_editor_modal.regenerate_body"
                    defaultMessage={
                      'Replace all participants of this cycle with a ' +
                      'completely new list of your current active employees? ' +
                      '{lineBreak}{lineBreak}' +
                      'Note: If you made significant changes to your ' +
                      'employee list, you may want to do this to save you ' +
                      'time from manually adding and editing the list. ' +
                      'Otherwise, you should cancel and edit the existing ' +
                      'participants instead. This feature is only available ' +
                      "before the cycle's start date."
                    }
                    values={{
                      lineBreak: <br />,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="app.views.administration.campaign_participants_snapshot_editor_modal.snapshot_body"
                    defaultMessage="Confirm will take a snapshot of each person in the system. <link>Learn more</link>"
                    values={{
                      link: (chunks) => (
                        <>
                          <a
                            className="text-link"
                            id="campaign_participants_snapshot_editor_modal_learn_more"
                          >
                            {chunks}
                          </a>
                          <SafeUncontrolledPopover
                            placement="top"
                            trigger="hover"
                            target="campaign_participants_snapshot_editor_modal_learn_more"
                          >
                            <FormattedMessage
                              id="app.views.administration.campaign_participants_snapshot_editor_modal.helper_text"
                              defaultMessage="The cycle participants snapshot, also known as a dataset, captures everyone's manager, department, etc. for use in this cycle. Once taken, any changes in Confirm to people outside of the cycle, e.g. manager and department changes, will not affect this snapshot. You can manually update this snapshot after taking it."
                            />
                          </SafeUncontrolledPopover>
                        </>
                      ),
                    }}
                  />
                )}
              </div>
              {inputs}
              {isRegeneration ? (
                <div className="modal-footer border-0 px-0">
                  <Row className="w-100 gx-2">
                    <Col className="col-6">
                      <Button
                        className="btn btn-light w-100"
                        onClick={onToggle}
                      >
                        <FormattedMessage
                          id="app.views.administration.campaign_participants_snapshot_editor_modal.button.cancel"
                          defaultMessage="Cancel"
                        />
                      </Button>
                    </Col>
                    <Col className="col-6">
                      <Button className="btn btn-danger w-100" type="submit">
                        <FormattedMessage
                          id="app.views.administration.campaign_participants_snapshot_editor_modal.button.regenerate"
                          defaultMessage="Regenerate all cycle participants"
                        />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ) : (
                button
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default CampaignParticipantsSnapshotEditorModal;
